import { Card, Col, Flex, Row, Space, Typography, theme } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faFileShield, faStopwatch } from '@fortawesome/free-solid-svg-icons';
import { ConnectionTypeTag } from '@shared/components/ConnectionTypeTag';
import { CONNECTION_TYPES } from '@shared/constants/connectionTypes';
import { EMPTY_DISPLAY_VALUE } from '@shared/constants/display';
import { DISPLAY_DATE_VARIANTS, formatDateForDisplay } from '@shared/utils/dateUtils';
import { StateTag } from './StateTag';
import { formatDuration } from './utils';

const { Text } = Typography;

const TIMING_COLUMN_MIN_WIDTH = 'min(12em, 100%)';

const TimingColumn = ({ title, value, details = [], flexGrow = 1 }) => (
  <Col
    flex={`${flexGrow} 1 0`}
    style={{ minWidth: TIMING_COLUMN_MIN_WIDTH }}
  >
    <Card.Grid
      hoverable={false}
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <Flex vertical gap={8}>
        <Text
          strong
          type='secondary'
          style={{
            fontSize: '0.9em',
          }}
        >
          {title}
        </Text>
        <Text
          strong
          style={{
            fontSize: '1.2em',
            lineHeight: 1,
          }}
        >
          {value || EMPTY_DISPLAY_VALUE}
        </Text>
        {details.length > 0 && (
          <Flex wrap style={{ rowGap: 4, columnGap: 16 }}>
            {details.map(({ label, value: detailValue }) => (
              <Flex key={label} vertical>
                <Text type='secondary' style={{ fontSize: '0.8em' }}>
                  {label}
                </Text>
                <Text code style={{ fontSize: '0.8em' }}>
                  {detailValue || EMPTY_DISPLAY_VALUE}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Card.Grid>
  </Col>
);

export const ExecutionOverview = ({ data }) => {
  const { token: themeToken } = theme.useToken();

  const quotationInsuranceCarrierId = data?.quotationInsuranceCarrierId;
  const insuranceCarrierName = data?.insuranceCarrier?.name;
  const connectionType = data?.connectionType;
  const state = data?.state;
  const isRpa = connectionType === CONNECTION_TYPES.RPA;

  const timing = data?.timing;
  const queuedAt = formatDateForDisplay(timing?.queuedAt, { variant: DISPLAY_DATE_VARIANTS.LOG });
  const startedAt = formatDateForDisplay(timing?.startedAt, { variant: DISPLAY_DATE_VARIANTS.LOG });
  const finishedAt = formatDateForDisplay(timing?.finishedAt, { variant: DISPLAY_DATE_VARIANTS.LOG });

  const durations = timing?.durations;
  const queueDuration = formatDuration(durations?.queueMs);
  const executionDuration = formatDuration(durations?.executionMs);
  const totalDuration = formatDuration(durations?.totalMs);

  return (
    <Flex vertical gap='middle'>
      <Flex gap='middle' wrap>
        <Card
          size='small'
          type='inner'
          title={(
            <Space style={{ color: themeToken.colorTextSecondary }}>
              <FontAwesomeIcon icon={faFileShield} />
              Cotización aseguradora
            </Space>
          )}
          style={{ flex: '3 1 24em', minWidth: 0 }}
        >
          <Flex gap='small' justify='space-between' align='center' wrap>
            <Flex vertical gap={2}>
              <Space wrap>
                <Text strong>{insuranceCarrierName}</Text>
                <ConnectionTypeTag connectionType={connectionType} compact={false} />
              </Space>
              <Text
                type='secondary'
                copyable
                ellipsis
                style={{
                  fontSize: '0.8em',
                }}
              >
                {quotationInsuranceCarrierId}
              </Text>
            </Flex>
            <StateTag state={state} />
          </Flex>
        </Card>
        <Card
          size='small'
          type='inner'
          title={(
            <Space style={{ color: themeToken.colorTextSecondary }}>
              <FontAwesomeIcon icon={faStopwatch} />
              Duración total
            </Space>
          )}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
          styles={{
            body: {
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
          }}
        >
          <Text
            strong
            style={{
              fontSize: '2em',
              lineHeight: 1,
            }}
          >
            {totalDuration || EMPTY_DISPLAY_VALUE}
          </Text>
        </Card>
      </Flex>
      <Card
        size='small'
        type='inner'
        title={(
          <Space style={{ color: themeToken.colorTextSecondary }}>
            <FontAwesomeIcon icon={faCalendar} />
            Tiempos
          </Space>
        )}
        style={{ overflow: 'hidden' }}
        styles={{ body: { padding: 0 } }}
      >
        <Row style={{ marginLeft: -1 }}>
          {isRpa && (
            <TimingColumn
              title='Cola'
              value={queueDuration}
              details={[
                { label: 'Fecha Encolado:', value: queuedAt },
              ]}
            />
          )}
          <TimingColumn
            flexGrow={2}
            title='Ejecución'
            value={executionDuration}
            details={[
              { label: 'Fecha Inicio:', value: startedAt },
              { label: 'Fecha Finalización:', value: finishedAt },
            ]}
          />
        </Row>
      </Card>
    </Flex>
  );
};
